import * as AccordionPrimitive from '@radix-ui/react-accordion'
import * as TabsPrimitive from '@radix-ui/react-tabs'
import { Check, ChevronDown, Minus, TrendingDown, TrendingUp } from 'lucide-react'
import { cn } from './lib/cn'

/* ==========================================================================
 * Card
 * ======================================================================== */

export function Card({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      className={cn('rounded-card border border-line bg-surface shadow-card', className)}
      {...props}
    />
  )
}

export function CardHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      className={cn('flex items-start justify-between gap-4 border-b border-line px-5 py-4', className)}
      {...props}
    />
  )
}

export function CardTitle({ className, ...props }: React.ComponentProps<'h3'>) {
  return <h3 className={cn('text-sm font-semibold text-foreground', className)} {...props} />
}

export function CardDescription({ className, ...props }: React.ComponentProps<'p'>) {
  return <p className={cn('mt-0.5 text-caption text-muted', className)} {...props} />
}

export function CardBody({ className, ...props }: React.ComponentProps<'div'>) {
  return <div className={cn('p-5', className)} {...props} />
}

export function CardFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      className={cn('flex items-center justify-end gap-2 border-t border-line px-5 py-3', className)}
      {...props}
    />
  )
}

/* ==========================================================================
 * SectionHeader
 * ======================================================================== */

export interface SectionHeaderProps {
  title: string
  description?: React.ReactNode
  /** Right-aligned actions, e.g. a filter or a Create button. */
  action?: React.ReactNode
  className?: string
}

export function SectionHeader({ title, description, action, className }: SectionHeaderProps) {
  return (
    <div className={cn('flex flex-wrap items-end justify-between gap-3', className)}>
      <div className="space-y-1">
        <h2 className="text-lg font-semibold tracking-tight text-foreground">{title}</h2>
        {description ? <p className="text-sm text-muted">{description}</p> : null}
      </div>
      {action}
    </div>
  )
}

/* ==========================================================================
 * StatCard
 * ======================================================================== */

export interface StatCardProps {
  label: string
  value: React.ReactNode
  /** Percentage or absolute change. Sign drives the colour and arrow. */
  delta?: number | null
  /** Shown after the delta, e.g. "vs yesterday". */
  deltaLabel?: string
  /** Small print below the value. */
  hint?: React.ReactNode
  icon?: React.ReactNode
  /** Rendered to the right — a sparkline or a meter. */
  visual?: React.ReactNode
  className?: string
}

export function StatCard({
  label,
  value,
  delta,
  deltaLabel,
  hint,
  icon,
  visual,
  className,
}: StatCardProps) {
  const hasDelta = typeof delta === 'number' && Number.isFinite(delta)
  const positive = hasDelta && delta >= 0
  const DeltaIcon = positive ? TrendingUp : TrendingDown

  return (
    <div
      className={cn(
        'rounded-card border border-line bg-surface p-5 shadow-card',
        className
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <p className="text-caption font-medium uppercase tracking-widest text-subtle">{label}</p>
        {icon ? <span className="text-subtle [&_svg]:h-[18px] [&_svg]:w-[18px]">{icon}</span> : null}
      </div>

      <div className="mt-3 flex items-end justify-between gap-3">
        <p className="font-mono text-2xl font-semibold tracking-tight text-foreground">{value}</p>
        {visual ? <div className="shrink-0">{visual}</div> : null}
      </div>

      {hasDelta || hint ? (
        <div className="mt-2 flex items-center gap-2 text-caption">
          {hasDelta ? (
            <span
              className={cn(
                'inline-flex items-center gap-1 font-medium',
                positive ? 'text-success-text' : 'text-danger-text'
              )}
            >
              <DeltaIcon className="h-3.5 w-3.5" />
              {positive ? '+' : ''}
              {delta}%
            </span>
          ) : null}
          {deltaLabel ? <span className="text-subtle">{deltaLabel}</span> : null}
          {hint ? <span className="text-muted">{hint}</span> : null}
        </div>
      ) : null}
    </div>
  )
}

/* ==========================================================================
 * EmptyState
 * ======================================================================== */

export interface EmptyStateProps {
  icon?: React.ReactNode
  title: string
  description?: React.ReactNode
  action?: React.ReactNode
  className?: string
}

export function EmptyState({ icon, title, description, action, className }: EmptyStateProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-3 rounded-card border border-dashed border-line-strong bg-sunken/40 px-6 py-12 text-center',
        className
      )}
    >
      {icon ? (
        <span className="flex h-11 w-11 items-center justify-center rounded-full bg-surface text-subtle shadow-card [&_svg]:h-5 [&_svg]:w-5">
          {icon}
        </span>
      ) : null}
      <div className="space-y-1">
        <p className="text-sm font-medium text-foreground">{title}</p>
        {description ? (
          <p className="mx-auto max-w-sm text-caption text-muted">{description}</p>
        ) : null}
      </div>
      {action}
    </div>
  )
}

/* ==========================================================================
 * Callout / Banner
 * ======================================================================== */

type Tone = 'info' | 'success' | 'warning' | 'danger' | 'neutral'

const TONE_CLASSES: Record<Tone, string> = {
  info: 'border-info/30 bg-info-soft text-info-text',
  success: 'border-success/30 bg-success-soft text-success-text',
  warning: 'border-warning/30 bg-warning-soft text-warning-text',
  danger: 'border-danger/30 bg-danger-soft text-danger-text',
  neutral: 'border-line bg-sunken text-muted',
}

export interface CalloutProps {
  tone?: Tone
  title?: React.ReactNode
  icon?: React.ReactNode
  action?: React.ReactNode
  className?: string
  children?: React.ReactNode
}

export function Callout({
  tone = 'info',
  title,
  icon,
  action,
  className,
  children,
}: CalloutProps) {
  return (
    <div
      role={tone === 'danger' ? 'alert' : undefined}
      className={cn('flex items-start gap-3 rounded-card border px-4 py-3', TONE_CLASSES[tone], className)}
    >
      {icon ? <span className="mt-0.5 shrink-0 [&_svg]:h-4 [&_svg]:w-4">{icon}</span> : null}
      <div className="min-w-0 flex-1 space-y-1">
        {title ? <p className="text-sm font-medium">{title}</p> : null}
        {children ? <div className="text-caption [&_a]:underline">{children}</div> : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  )
}

/** Full-bleed strip for page-level notices. */
export function Banner({ className, ...props }: CalloutProps) {
  return <Callout className={cn('rounded-none border-x-0 border-t-0', className)} {...props} />
}

/* ==========================================================================
 * Divider
 * ======================================================================== */

export interface DividerProps {
  /** Text in the middle of the rule, e.g. "or". */
  label?: React.ReactNode
  className?: string
}

export function Divider({ label, className }: DividerProps) {
  if (!label) return <hr className={cn('border-t border-line', className)} />

  return (
    <div className={cn('flex items-center gap-3', className)}>
      <span className="h-px flex-1 bg-line" />
      <span className="text-caption text-subtle">{label}</span>
      <span className="h-px flex-1 bg-line" />
    </div>
  )
}

/* ==========================================================================
 * DescriptionList
 * ======================================================================== */

export interface DescriptionItem {
  term: React.ReactNode
  detail: React.ReactNode
}

export interface DescriptionListProps {
  items: DescriptionItem[]
  className?: string
  /** One column by default; two on wider screens. */
  columns?: 1 | 2
}

export function DescriptionList({ items, className, columns = 1 }: DescriptionListProps) {
  return (
    <dl
      className={cn(
        'grid gap-x-8 gap-y-4',
        columns === 2 ? 'sm:grid-cols-2' : 'grid-cols-1',
        className
      )}
    >
      {items.map((item, index) => (
        <div key={index} className="min-w-0 space-y-1">
          <dt className="text-caption font-medium uppercase tracking-widest text-subtle">
            {item.term}
          </dt>
          <dd className="break-words text-sm text-foreground">{item.detail}</dd>
        </div>
      ))}
    </dl>
  )
}

/* ==========================================================================
 * Tabs
 * ======================================================================== */

export const Tabs = TabsPrimitive.Root

export function TabsList({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.List>) {
  return (
    <TabsPrimitive.List
      className={cn(
        'inline-flex items-center gap-1 rounded-control border border-line bg-sunken p-0.5',
        className
      )}
      {...props}
    />
  )
}

export function TabsTrigger({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Trigger>) {
  return (
    <TabsPrimitive.Trigger
      className={cn(
        'inline-flex h-8 items-center gap-1.5 rounded-[9px] px-3 text-sm font-medium text-muted',
        'transition-colors hover:text-foreground',
        'data-[state=active]:bg-surface data-[state=active]:text-foreground data-[state=active]:shadow-card',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus/40',
        'disabled:cursor-not-allowed disabled:opacity-50',
        className
      )}
      {...props}
    />
  )
}

export function TabsContent({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Content>) {
  return (
    <TabsPrimitive.Content
      className={cn('mt-4 focus-visible:outline-none', className)}
      {...props}
    />
  )
}

/* ==========================================================================
 * Accordion
 * ======================================================================== */

export interface AccordionItem {
  value: string
  trigger: React.ReactNode
  content: React.ReactNode
  disabled?: boolean
}

export interface AccordionProps {
  items: AccordionItem[]
  /** Allow several panels open at once. */
  multiple?: boolean
  defaultValue?: string[]
  className?: string
}

export function Accordion({ items, multiple = false, defaultValue, className }: AccordionProps) {
  const body = items.map((item) => (
    <AccordionPrimitive.Item key={item.value} value={item.value} disabled={item.disabled}>
      <AccordionPrimitive.Header>
        <AccordionPrimitive.Trigger
          className={cn(
            'group flex w-full items-center justify-between gap-3 px-5 py-3.5 text-left',
            'text-sm font-medium text-foreground transition-colors hover:bg-sunken',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus/40 focus-visible:ring-inset',
            'disabled:cursor-not-allowed disabled:opacity-50'
          )}
        >
          {item.trigger}
          <ChevronDown className="h-4 w-4 shrink-0 text-subtle transition-transform duration-200 group-data-[state=open]:rotate-180" />
        </AccordionPrimitive.Trigger>
      </AccordionPrimitive.Header>
      <AccordionPrimitive.Content className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
        <div className="px-5 pb-4 text-sm text-muted">{item.content}</div>
      </AccordionPrimitive.Content>
    </AccordionPrimitive.Item>
  ))

  const shell = cn(
    'divide-y divide-line overflow-hidden rounded-card border border-line bg-surface',
    className
  )

  // Radix models single and multiple as a discriminated union, so these have
  // to be two distinct elements rather than one with a dynamic `type`.
  if (multiple) {
    return (
      <AccordionPrimitive.Root type="multiple" defaultValue={defaultValue} className={shell}>
        {body}
      </AccordionPrimitive.Root>
    )
  }

  return (
    <AccordionPrimitive.Root
      type="single"
      collapsible
      defaultValue={defaultValue?.[0]}
      className={shell}
    >
      {body}
    </AccordionPrimitive.Root>
  )
}

/* ==========================================================================
 * Stepper
 * ======================================================================== */

export interface StepperStep {
  label: string
  description?: string
}

export interface StepperProps {
  steps: StepperStep[]
  /** Zero-based index of the current step. */
  current: number
  className?: string
}

export function Stepper({ steps, current, className }: StepperProps) {
  return (
    <ol className={cn('flex flex-col gap-4 sm:flex-row sm:items-start sm:gap-2', className)}>
      {steps.map((step, index) => {
        const complete = index < current
        const active = index === current

        return (
          <li key={step.label} className="flex flex-1 items-start gap-3">
            <span
              className={cn(
                'flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-caption font-semibold',
                complete && 'border-accent bg-accent text-accent-foreground',
                active && 'border-accent text-accent',
                !complete && !active && 'border-line-strong text-subtle'
              )}
              aria-current={active ? 'step' : undefined}
            >
              {complete ? <Check className="h-3.5 w-3.5" strokeWidth={3} /> : index + 1}
            </span>
            <div className="min-w-0 space-y-0.5 pt-0.5">
              <p
                className={cn(
                  'text-sm font-medium',
                  active || complete ? 'text-foreground' : 'text-subtle'
                )}
              >
                {step.label}
              </p>
              {step.description ? (
                <p className="text-caption text-muted">{step.description}</p>
              ) : null}
            </div>
          </li>
        )
      })}
    </ol>
  )
}

/* ==========================================================================
 * Meter — usage against a quota, threshold-coloured
 * ======================================================================== */

export interface MeterProps {
  value: number
  max: number
  label?: React.ReactNode
  /** Formats the numbers shown to the right of the label. */
  format?: (value: number, max: number) => string
  className?: string
}

/**
 * Usage bar. Colour tracks the thresholds the console and account pages share:
 * calm below 70%, amber to 90%, red beyond — the same breakpoints the quota
 * banners use, so the two never disagree.
 */
export function Meter({ value, max, label, format, className }: MeterProps) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0
  const tone = pct >= 90 ? 'danger' : pct >= 70 ? 'warning' : 'success'

  const barClass = {
    success: 'bg-success',
    warning: 'bg-warning',
    danger: 'bg-danger',
  }[tone]

  const textClass = {
    success: 'text-success-text',
    warning: 'text-warning-text',
    danger: 'text-danger-text',
  }[tone]

  const display = format ? format(value, max) : `${Math.round(pct)}%`

  return (
    <div className={cn('space-y-1.5', className)}>
      {label || display ? (
        <div className="flex items-baseline justify-between gap-3 text-caption">
          <span className="text-muted">{label}</span>
          <span className={cn('font-mono font-medium', textClass)}>{display}</span>
        </div>
      ) : null}
      <div
        className="h-1.5 w-full overflow-hidden rounded-full bg-sunken"
        role="progressbar"
        aria-valuenow={Math.round(pct)}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className={cn('h-full rounded-full transition-[width] duration-500', barClass)}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  )
}

/* ==========================================================================
 * PlaceholderBadge — marks preview data
 * ======================================================================== */

/**
 * Stamped on any panel rendering fixture data because the platform does not
 * serve that endpoint yet. Makes a demo impossible to mistake for live data.
 */
export function PreviewDataBadge({ className }: { className?: string }) {
  return (
    <span
      title="This page renders placeholder data. See docs/PLATFORM_API_GAPS.md."
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border border-warning/40 bg-warning-soft px-2 py-0.5 text-[11px] font-medium text-warning-text',
        className
      )}
    >
      <Minus className="h-3 w-3" strokeWidth={3} />
      Preview data
    </span>
  )
}
